import React, { useState, useEffect, useRef } from 'react';
import { SideNav } from './SideNav';
import { useAuth } from '../context/AuthContext';
import { cvService } from '../services/cvService';
import { PageLayout } from './ui';

export default function Files() {
  const { sideNavLocked, setSideNavLocked } = useAuth();
  const [cvs, setCvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showActionMenu, setShowActionMenu] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    file: null
  });

  // PDF Viewer state
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [currentPDFUrl, setCurrentPDFUrl] = useState(null);
  const [currentPDFName, setCurrentPDFName] = useState('');
  const [loadingPDF, setLoadingPDF] = useState(false);

  // Rename modal state
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renamingCV, setRenamingCV] = useState(null);
  const [renameForm, setRenameForm] = useState({ name: '' });
  const [renaming, setRenaming] = useState(false);
  const [renameError, setRenameError] = useState(null);

  useEffect(() => {
    loadCVs();
  }, []);

  const loadCVs = async () => {
    try {
      setLoading(true);
      const data = await cvService.getAllCVs();
      setCvs(data);
      setError(null);
    } catch (err) {
      console.error('Error loading CVs:', err);
      setError('Failed to load CVs');
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!uploadForm.file) {
      setUploadError('Please select a file');
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);

      await cvService.uploadCV(uploadForm.file, {});
      
      // Reset form and close modal
      setUploadForm({ file: null });
      setShowUploadModal(false);
      
      // Reload CVs
      await loadCVs();
      
    } catch (err) {
      console.error('Error uploading CV:', err);
      setUploadError(err.message || 'Failed to upload CV');
    } finally {
      setUploading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setUploadError('Please select a PDF file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setUploadError('File size must be less than 10MB');
        return;
      }
      
      setUploadForm(prev => ({ 
        ...prev, 
        file: file
      }));
      setUploadError(null);
    }
  };

  const openUploadModal = () => {
    setShowUploadModal(true);
    setUploadError(null);
    setUploadForm({ file: null });
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Generate display name for CV
  const getDisplayName = (cv, index) => {
    // Prioritize user-defined label over original filename
    if (cv.label) {
      return cv.label;
    }
    if (cv.originalName) {
      return cv.originalName.replace(/\.[^/.]+$/, ''); // Remove file extension
    }
    return cv.displayName || `RES-${String(index + 1).padStart(3, '0')}`;
  };

  // Filter CVs based on search query
  const filteredCVs = cvs.filter(cv => {
    const searchLower = searchQuery.toLowerCase();
    const name = getDisplayName(cv, cvs.indexOf(cv)).toLowerCase();
    const tags = (cv.tags || []).join(' ').toLowerCase();
    return name.includes(searchLower) || tags.includes(searchLower);
  });

  // Handle action menu
  const handleActionClick = (cvId, event) => {
    event.stopPropagation();
    const newState = showActionMenu === cvId ? null : cvId;
    setShowActionMenu(newState);
  };

  const handleActionSelect = async (action, cv) => {
    setShowActionMenu(null);
    
    if (action === 'delete') {
      if (window.confirm(`Are you sure you want to delete "${getDisplayName(cv, cvs.indexOf(cv))}"? This action cannot be undone.`)) {
        try {
          await cvService.deleteCV(cv._id);
          // Reload CVs to reflect the deletion
          await loadCVs();
        } catch (error) {
          console.error('Error deleting CV:', error);
          alert('Failed to delete CV. Please try again.');
        }
      }
    } else {
      // TODO: Implement other actions
      console.log(`Action: ${action} for CV:`, cv);
    }
  };

  // Handle PDF viewing
  const handleViewPDF = async (cv) => {
    setShowActionMenu(null);
    setLoadingPDF(true);
    
    try {
      console.log('Attempting to load PDF for CV:', cv._id);
      const pdfUrl = await cvService.getCVViewingURL(cv._id);
      console.log('PDF URL generated successfully');
      setCurrentPDFUrl(pdfUrl);
      setCurrentPDFName(getDisplayName(cv, cvs.indexOf(cv)));
      setShowPDFViewer(true);
    } catch (error) {
      console.error('Error loading PDF:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        cvId: cv._id
      });
      
      // Show more specific error message
      let errorMessage = 'Failed to load PDF. ';
      if (error.message.includes('404')) {
        errorMessage += 'File not found.';
      } else if (error.message.includes('401') || error.message.includes('403')) {
        errorMessage += 'Authentication error. Please log in again.';
      } else if (error.message.includes('500')) {
        errorMessage += 'Server error. Please try again later.';
      } else {
        errorMessage += 'Please try again.';
      }
      
      alert(errorMessage);
    } finally {
      setLoadingPDF(false);
    }
  };

  // Handle CV renaming
  const handleRename = (cv) => {
    setShowActionMenu(null);
    setRenamingCV(cv);
    setRenameForm({ name: getDisplayName(cv, cvs.indexOf(cv)) });
    setRenameError(null);
    setShowRenameModal(true);
  };

  // Submit rename
  const handleRenameSubmit = async () => {
    if (!renameForm.name.trim()) {
      setRenameError('Name cannot be empty');
      return;
    }

    try {
      setRenaming(true);
      setRenameError(null);

      // Update the CV with new label (display name)
      await cvService.updateCV(renamingCV._id, { 
        label: renameForm.name.trim()
      });

      // Close modal and reload CVs
      setShowRenameModal(false);
      setRenamingCV(null);
      await loadCVs();
      
    } catch (error) {
      console.error('Error renaming CV:', error);
      setRenameError('Failed to rename file. Please try again.');
    } finally {
      setRenaming(false);
    }
  };

  // Close PDF viewer
  const closePDFViewer = () => {
    setShowPDFViewer(false);
    if (currentPDFUrl && currentPDFUrl.startsWith('blob:')) {
      window.URL.revokeObjectURL(currentPDFUrl);
    }
    setCurrentPDFUrl(null);
    setCurrentPDFName('');
  };

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowActionMenu(null);
    if (showActionMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showActionMenu]);

  if (loading) {
    return (
      <>
        <SideNav onLockChange={setSideNavLocked} />
        <div className={`min-h-screen transition-all duration-300 ${sideNavLocked ? 'ml-64' : 'ml-0'}`}>
          <PageLayout>
            <div className={`py-8 px-6 ${!sideNavLocked ? 'ml-16' : ''}`}>
              <div className="text-white text-center">Loading docs...</div>
            </div>
          </PageLayout>
        </div>
      </>
    );
  }

  return (
    <>
      <SideNav onLockChange={setSideNavLocked} />
      <div className={`min-h-screen transition-all duration-300 ${sideNavLocked ? 'ml-64' : 'ml-0'}`}>
        <PageLayout>
          {/* Main Content */}
          <div className={`py-8 px-6 ${!sideNavLocked ? 'ml-16' : ''}`}>
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">Docs</h1>
            </div>

            {error ? (
              <div className="bg-white bg-opacity-10 rounded-lg border border-gray-200 border-opacity-20 max-w-4xl">
                <div className="p-8 text-center">
                  <div className="text-red-400 mb-4">
                    <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-red-300 mb-1">Error loading docs</h3>
                  <p className="text-gray-500">{error}</p>
                </div>
              </div>
            ) : cvs.length === 0 ? (
              <div className="bg-white bg-opacity-10 rounded-lg border border-gray-200 border-opacity-20 max-w-4xl">
                <div className="p-8 text-center text-gray-400">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-300 mb-1">No docs yet</h3>
                  <p className="text-gray-500 mb-6">Upload your first CV to get started with smart job matching</p>
                  
                  <button 
                    onClick={openUploadModal}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Upload CV
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Search Bar */}
                <div className="mb-6">
                  <div className="relative max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Search docs..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:bg-opacity-15 transition-all duration-200"
                    />
                  </div>
                </div>

                {/* Table */}
                <div className="bg-white bg-opacity-5 rounded-lg border border-gray-200 border-opacity-20">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      {/* Table Header */}
                      <thead className="border-b border-gray-200 border-opacity-20">
                        <tr>
                          <th className="text-left py-4 px-6 text-gray-300 font-medium text-sm">Name</th>
                          <th className="text-left py-4 px-6 text-gray-300 font-medium text-sm">Used in</th>
                          <th className="text-left py-4 px-6 text-gray-300 font-medium text-sm">Date Added</th>
                          <th className="w-12 py-4 px-6"></th>
                        </tr>
                      </thead>

                      {/* Table Body */}
                      <tbody>
                        {filteredCVs.map((cv, index) => (
                          <tr key={cv._id} className="border-b border-gray-200 border-opacity-10 hover:bg-white hover:bg-opacity-5 transition-colors duration-200">
                            {/* Name */}
                            <td className="py-4 px-6">
                              <div className="text-white font-medium">
                                {getDisplayName(cv, index)}
                              </div>
                            </td>

                            {/* Used in */}
                            <td className="py-4 px-6">
                              <span className="inline-flex items-center justify-center w-8 h-6 bg-gray-600 text-white text-sm rounded-full">
                                {cv.usedInJobs ? cv.usedInJobs.length : 0}
                              </span>
                            </td>

                            {/* Date Added */}
                            <td className="py-4 px-6">
                              <div className="text-gray-300">
                                {formatDate(cv.createdAt)}
                              </div>
                            </td>

                            {/* Actions */}
                            <td className="py-4 px-6 relative">
                              <button
                                onClick={(e) => handleActionClick(cv._id, e)}
                                className="text-gray-400 hover:text-white transition-colors duration-200 p-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                aria-label="Open actions menu"
                                id={`action-button-${cv._id}`}
                              >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Empty State for Filtered Results */}
                  {filteredCVs.length === 0 && searchQuery && (
                    <div className="p-8 text-center text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <h3 className="text-lg font-medium text-gray-300 mb-1">No docs found</h3>
                      <p className="text-gray-500">Try adjusting your search terms</p>
                    </div>
                  )}

                  {/* Add File Button */}
                  <div className="p-6 border-t border-gray-200 border-opacity-10">
                    <button 
                      onClick={openUploadModal}
                      className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-200"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Doc
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </PageLayout>

        {/* Action Dropdown Menu - Rendered outside table to avoid clipping */}
        {showActionMenu && (() => {
          const button = document.getElementById(`action-button-${showActionMenu}`);
          const rect = button?.getBoundingClientRect();
          const cv = cvs.find(cv => cv._id === showActionMenu);
          return (
            <div
              className="fixed z-[999] w-36 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5"
              style={{
                top: rect ? rect.bottom + 8 : '50%',
                left: rect ? rect.right - 144 : '50%', // 144px = w-36
              }}
            >
              <div className="py-1" role="menu">
                <button
                  onClick={() => handleViewPDF(cv)}
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                  role="menuitem"
                  disabled={loadingPDF}
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {loadingPDF ? 'Loading...' : 'View'}
                </button>
                <button
                  onClick={() => handleRename(cv)}
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                  role="menuitem"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Rename
                </button>
                <button
                  onClick={() => handleActionSelect('delete', cv)}
                  className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700"
                  role="menuitem"
                >
                  <svg className="w-4 h-4 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9zM4 5a2 2 0 012-2h8a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zM8 8a1 1 0 012 0v3a1 1 0 11-2 0V8zm4 0a1 1 0 112 0v3a1 1 0 11-2 0V8z" clipRule="evenodd" />
                  </svg>
                  Delete
                </button>
              </div>
            </div>
          );
        })()}

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Add Doc</h3>
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {uploadError && (
                  <div className="mb-4 p-3 bg-red-900 bg-opacity-50 border border-red-500 rounded text-red-200 text-sm">
                    {uploadError}
                  </div>
                )}

                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full p-8 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-blue-500 hover:text-blue-400 transition-colors duration-200 flex flex-col items-center gap-4"
                  >
                    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <div className="text-center">
                      <div className="text-lg font-medium">
                        {uploadForm.file ? uploadForm.file.name : 'Select your document'}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {uploadForm.file ? 'Click to change file' : 'PDF files only, up to 10MB'}
                      </div>
                    </div>
                  </button>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="flex-1 px-4 py-2 text-gray-300 hover:text-white transition-colors duration-200"
                    disabled={uploading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={uploading || !uploadForm.file}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PDF Viewer Modal */}
        {showPDFViewer && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-6xl max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {currentPDFName}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const cv = cvs.find(cv => getDisplayName(cv, cvs.indexOf(cv)) === currentPDFName);
                      if (cv) {
                        cvService.downloadCV(cv._id, currentPDFName + '.pdf');
                      }
                    }}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    title="Download PDF"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </button>
                  <button
                    onClick={closePDFViewer}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-hidden">
                {currentPDFUrl ? (
                  <iframe
                    src={currentPDFUrl}
                    className="w-full h-full border-0"
                    title={currentPDFName}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-gray-500">Loading PDF...</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Rename Modal */}
        {showRenameModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Rename Document</h3>
                  <button
                    onClick={() => setShowRenameModal(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {renameError && (
                  <div className="mb-4 p-3 bg-red-900 bg-opacity-50 border border-red-500 rounded text-red-200 text-sm">
                    {renameError}
                  </div>
                )}

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Document Name
                  </label>
                  <input
                    type="text"
                    value={renameForm.name}
                    onChange={(e) => setRenameForm({ name: e.target.value })}
                    placeholder="Enter document name..."
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    disabled={renaming}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleRenameSubmit();
                      }
                    }}
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowRenameModal(false)}
                    className="flex-1 px-4 py-2 text-gray-300 hover:text-white transition-colors duration-200"
                    disabled={renaming}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRenameSubmit}
                    disabled={renaming || !renameForm.name.trim()}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {renaming ? 'Renaming...' : 'Rename'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
} 